import React, { useState, useCallback } from "react";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import SurveyPage1 from "./SurveyPage1";
import SurveyPage2 from "./SurveyPage2";
import SurveyPage3 from "./SurveyPage3";
import SurveyPage4 from "./SurveyPage4";
import SurveyPage5 from "./SurveyPage5";
import ResultPage from "./ResultPage";
import {
  getRecommendations,
  generateHealthManual,
} from "../utils/cancerRecommendations";
import { getHealthScreeningRecommendations } from "../utils/healthScreeningRecommendations";
import "../styles/components/MultiPageSurvey.css";

const MultiPageSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [surveyData, setSurveyData] = useState({
    // Page 1: Basic Demographics
    biologicalSex: "",
    ethnicity: "",
    age: "",
    location: "",

    // Page 2: Physical Measurements
    weight: "",
    height: "",

    // Page 3: Family History
    familyCancerHistory: [],

    // Page 4: Personal Health
    chronicDiseases: [],
    bloodSugarStatus: "",
    cholesterolStatus: "",
    bloodPressureStatus: "",
    smokingHistory: "",
    cigarettesPerDay: "",
    livedWithSmoker: "",
    radiationExposure: "",

    // Page 5: Lifestyle
    alcoholConsumption: "",
    dietHabits: "",
  });

  const [analysisResults, setAnalysisResults] = useState(null);

  // Store current question state for each page
  const [pageStates, setPageStates] = useState({
    1: 0, // Page 1 starts at question 0
    2: 0, // Page 2 starts at question 0
    3: 0, // Page 3 starts at question 0
    4: 0, // Page 4 starts at question 0
    5: 0, // Page 5 starts at question 0
  });

  const updateSurveyData = useCallback((field, value) => {
    setSurveyData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updatePageState = useCallback((pageNum, questionIndex) => {
    setPageStates((prev) => ({
      ...prev,
      [pageNum]: questionIndex,
    }));
  }, []);

  const getPageState = (pageNum) => {
    return pageStates[pageNum] || 0;
  };

  const nextPage = useCallback(() => {
    const currentPath = location.pathname;
    if (currentPath.includes("/survey/")) {
      const currentPageNum = parseInt(currentPath.split("/").pop());
      if (currentPageNum < 5) {
        navigate(`/survey/${currentPageNum + 1}`);
      }
    }
  }, [location.pathname, navigate]);

  const prevPage = useCallback(() => {
    const currentPath = location.pathname;
    if (currentPath.includes("/survey/")) {
      const currentPageNum = parseInt(currentPath.split("/").pop());
      if (currentPageNum > 1) {
        navigate(`/survey/${currentPageNum - 1}`);
      } else {
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  const handleComplete = () => {
    // Analyze survey data and generate recommendations
    const { stronglyRecommend, recommend, belowAverage } = getRecommendations(surveyData);
    const healthManual = generateHealthManual(surveyData);
    const healthScreening = getHealthScreeningRecommendations(surveyData);

    // Combine all data for the result page
    const results = {
      surveyData,
      recommendations: {
        stronglyRecommend,
        recommend,
        belowAverage,
      },
      healthManual,
      healthScreening,
    };

    // Store results in localStorage for the results page
    localStorage.setItem("omnicare_analysis_results", JSON.stringify(results));

    // Navigate to feature vote page (then results)
    navigate("/vote");
  };

  // Get current section from URL for display purposes
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes("/survey/")) {
      const pageNum = parseInt(path.split("/").pop());
      if (pageNum >= 1 && pageNum <= 5) {
        return pageNum;
      }
    }
    return 1;
  };

  return (
    <div>
      <Routes>
        <Route
          path="1"
          element={
            <SurveyPage1
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onNext={nextPage}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
              initialQuestionIndex={getPageState(1)}
              updatePageState={updatePageState}
              pageNumber={1}
            />
          }
        />
        <Route
          path="2"
          element={
            <SurveyPage2
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onNext={nextPage}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
              initialQuestionIndex={getPageState(2)}
              updatePageState={updatePageState}
              pageNumber={2}
            />
          }
        />
        <Route
          path="3"
          element={
            <SurveyPage3
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onNext={nextPage}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
              initialQuestionIndex={getPageState(3)}
              updatePageState={updatePageState}
              pageNumber={3}
            />
          }
        />
        <Route
          path="4"
          element={
            <SurveyPage4
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onNext={nextPage}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
              initialQuestionIndex={getPageState(4)}
              updatePageState={updatePageState}
              pageNumber={4}
            />
          }
        />
        <Route
          path="5"
          element={
            <SurveyPage5
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onComplete={handleComplete}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
              initialQuestionIndex={getPageState(5)}
              updatePageState={updatePageState}
              pageNumber={5}
            />
          }
        />
        <Route
          path="*"
          element={
            <SurveyPage1
              surveyData={surveyData}
              updateSurveyData={updateSurveyData}
              onNext={nextPage}
              onNavigationBack={prevPage}
              currentSection={getCurrentSection()}
            />
          }
        />
      </Routes>
    </div>
  );
};

export default MultiPageSurvey;
